import { Fragment, type ReactNode } from 'react';
import { SectionHeader } from './SectionHeader';

// Generic "history of lots" section: a header with a count, a column of cards,
// and a "Show N more" affordance. Data-agnostic — the parent owns the data, the
// slicing (`items` is already the visible slice), and the card type via
// `renderItem`. Cards carry their own reveal cascade, so this stays presentational.
type Props<T extends { id: string }> = {
  label: string;
  count: number;
  accent?: string;
  animateHeader?: boolean;
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  canShowMore: boolean;
  showMoreCount: number;
  onShowMore: () => void;
  isLoadingMore?: boolean;
};

export function LotHistoryList<T extends { id: string }>({
  label,
  count,
  accent,
  animateHeader,
  items,
  renderItem,
  canShowMore,
  showMoreCount,
  onShowMore,
  isLoadingMore = false,
}: Props<T>) {
  return (
    <>
      <SectionHeader label={label} count={count} accent={accent} animate={animateHeader} />
      <div className="px-3 flex flex-col gap-2">
        {items.map((item, i) => (
          <Fragment key={item.id}>{renderItem(item, i)}</Fragment>
        ))}
      </div>
      {canShowMore && (
        <div className="px-3 mt-2">
          <button
            type="button"
            onClick={onShowMore}
            disabled={isLoadingMore}
            className="w-full font-mono text-[11px] tracking-wider text-fg-muted border border-edge/70 py-2.5 hover:border-edge-bright hover:text-fg transition-colors disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading…' : `Show ${showMoreCount} more`}
          </button>
        </div>
      )}
    </>
  );
}
